/*
 * Browser opens of first-set screens (OVIO-106): one POST per pathname change to a mapped screen.
 * A vehicle page counts once per vehicle: moving between its sub-routes (maintenances, documents...)
 * is not a new open, entering another vehicle is.
 * Not through the Redux API middleware, which toasts on every non-GET. Not getCsrfToken(): it decodes
 * the whole cookie string and throws on a bare '%'. axios adds X-CSRFToken from the defaults set in
 * middleware/api.js. A lost event is lost: no retry, no queue.
 */
import axios from 'axios';
import { matchPath } from 'react-router';
import { isCareServiceRoute } from './routes';
import { SERVICE_ADMIN, SERVICE_CHAIN, SERVICE_FLEET, SERVICE_PERSONAL, SERVICE_WORKSHOP } from './utils';

const exact = (path, screen) => ({ path, screen, exact: true });
const prefix = (path, screen) => ({ path, screen, exact: false });
// Matches the page and every sub-route; the matched base URL, not the pathname, is the dedupe key.
const page = (path, screen) => ({ path, screen, exact: false, perMatch: true });

const SCREENS = {
  [SERVICE_PERSONAL]: [
    exact('/garage', 'owner_vehicle_list'),
    page('/vehicle/:vehicleId', 'owner_vehicle_page'),
  ],
  [SERVICE_FLEET]: [
    exact('/', 'fleet_vehicle_list'),
    exact('/organization_garage/:organizationId', 'fleet_vehicle_list'),
    exact('/organization_garage/:organizationId/vehicles', 'fleet_vehicle_list'),
    exact('/fleet-map', 'fleet_map'),
    exact('/fleet-location/:vehicleId', 'fleet_vehicle_history'),
  ],
  [SERVICE_WORKSHOP]: [
    page('/workshop-vehicle/:vehicleId', 'workshop_vehicle_page'),
  ],
  [SERVICE_CHAIN]: [
    exact('/', 'chain_home'),
    exact('/chain/:chainId', 'chain_home'),
    exact('/chain/:chainId/parts-usage', 'chain_home'),
    prefix('/chain/:chainId/invoicing', 'chain_invoice_list'),
  ],
  [SERVICE_ADMIN]: [
    exact('/staff_settings/staff_stats', 'admin_statistics'),
  ],
};

function openOf(service, pathname) {
  if (service === SERVICE_WORKSHOP && isCareServiceRoute(pathname)) {
    return { screen: pathname === '/' ? 'workshop_home' : 'workshop_care_service_list', key: pathname };
  }
  const routes = SCREENS[service] || [];
  for (let i = 0; i < routes.length; i += 1) {
    const match = matchPath(pathname, { path: routes[i].path, exact: routes[i].exact });
    if (match) {
      // '/vehicle/7/' and '/vehicle/7/maintenances' match as '/vehicle/7/' and '/vehicle/7'.
      return { screen: routes[i].screen, key: routes[i].perMatch ? match.url.replace(/\/$/, '') : pathname };
    }
  }
  return null;
}

export function screenFor(service, pathname) {
  const open = openOf(service, pathname);
  return open ? open.screen : null;
}

let started = false;

export function startUsageEvents(history, service) {
  if (started) {
    return;
  }
  started = true;
  let lastKey = null;
  const send = (location) => {
    const open = openOf(service, location.pathname);
    const key = open ? open.key : location.pathname;
    if (key === lastKey) {
      return;
    }
    lastKey = key;
    if (open) {
      axios.post('/uiapi/1/usage_event/', { screen: open.screen }).catch(() => {});
    }
  };
  history.listen(send);
  send(history.location);
}
